// Copyright (c) 2023 Ryan Piaskowy. All Rights Reserved.

const formatTimeToAMPM = (timeString) => {
  if (!timeString || timeString.trim() === "") return timeString || "";

  const time = new Date(`1970-01-01T${timeString}`);
  if (Number.isNaN(time.getTime())) return timeString;

  if (time.getSeconds() >= 30) time.setMinutes(time.getMinutes() + 1);
  time.setSeconds(0, 0);

  return time.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const splitTags = (value = "") =>
  value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

const splitDates = (value = "") =>
  value
    .split(",")
    .map((date) => date.trim())
    .filter(Boolean);

// Accepts common truthy spreadsheet values (TRUE, true, 1, yes). Anything
// else - including a missing/blank column - is treated as false so existing
// TSVs that don't have this column yet keep working unchanged.
const parseBoolean = (value = "") =>
  ["true", "1", "yes"].includes(value.trim().toLowerCase());

export function parseRegionsTsv(text) {
  return text
    .split(/\r?\n/)
    .slice(1)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [region = "", regionTsv = ""] = line.split("\t");
      return { region: region.trim(), regionTsv: regionTsv.trim() };
    })
    .filter(({ region, regionTsv }) => region && regionTsv);
}

// Columns are read positionally by destructuring, with a default for every
// field. That means the TSV export can grow new trailing columns (for future
// features) without breaking this parser: extra fields are simply ignored
// until a corresponding destructured name is added above, and rows exported
// before a new column existed just fall back to that field's default. Only
// insert new columns at the END of the TSV, never in the middle, or every
// field after the insertion point will shift and be misread.
export function parseEventsTsv(text, selectedRegion) {
  return text
    .split(/\r?\n/)
    .filter((line) => line.trim())
    .map((line) => {
      const fields = line.split("\t");
      const [
        id = "",
        submitTimestamp = "",
        imageUrl = "",
        eventWebsite = "",
        region = "",
        neighborhood = "",
        eventVenue = "",
        eventTitle = "",
        eventNotes = "",
        startTime = "",
        endTime = "",
        tags = "",
        reported = "",
        approved = "",
        eventDates = "",
        isDuplicate = "",
      ] = fields;

      return {
        id: id.trim(),
        submitTimestamp: submitTimestamp.trim(),
        imageUrl: imageUrl.trim(),
        eventWebsite: eventWebsite.trim(),
        region: region.trim(),
        neighborhood: neighborhood.trim(),
        eventVenue: eventVenue.trim(),
        eventTitle: eventTitle.trim(),
        eventNotes: eventNotes.trim(),
        startTime: formatTimeToAMPM(startTime.trim()),
        endTime: formatTimeToAMPM(endTime.trim()),
        tags: splitTags(tags),
        reported: reported.trim(),
        approved: approved.trim(),
        eventDates: splitDates(eventDates),
        isDuplicate: parseBoolean(isDuplicate),
      };
    })
    .filter((event) => event.region === selectedRegion && event.id);
}

export { formatTimeToAMPM };
